/**
 * QuickSightReport — embeds an Octopus-owned QuickSight dashboard inside Rocky.
 *
 * Ported from AWSOctopusPortal / AgentHubPortal. Fetches an embed URL from the
 * Octopus Flow Management service (see FlowManagementClient), then hands it to
 * the amazon-quicksight-embedding-sdk to render an interactive dashboard. Also
 * supports injecting parameters and updating filter groups per sheet after the
 * dashboard loads.
 */

import React, { useEffect, useRef, useState, useCallback } from 'react';
import { Box, Spinner } from '@amzn/awsui-components-react';
import {
  createEmbeddingContext,
  DashboardExperience,
  EmbeddingEvents,
  ExperienceFrameMetadata,
  FrameOptions,
  Parameter,
} from 'amazon-quicksight-embedding-sdk';
import {
  generateEmbedUrlForAnonymousUser,
  generateEmbedUrlForRegisteredUser,
} from '../../client/FlowManagementClient';

interface QuickSightReportProps {
  dashboardId?: string;
  height?: string | number;
  width?: string | number;
  parameters?: Parameter[];
  sheetId?: string | string[];
  filterGroups?: any[];
  style?: React.CSSProperties;
  visible?: boolean;
  accountShortName?: string;
  useRegisteredUser?: boolean;
  userAlias?: string;

  /** Generic single-parameter injection (optional). */
  paramName?: string;
  paramValues?: Array<string | number>;

  /** Generic multi-parameter injection (optional). Key = parameter name. */
  paramPatches?: Record<string, Array<string | number>>;
}

/* Shared defaults: a fresh `[]` per render would rebuild applyParamsAndFilters every render,
   and its effect (which sets isUpdating) would then re-run forever — with a parameter to set,
   the spinner never clears. */
const NO_PARAMETERS: Parameter[] = [];
const NO_FILTER_GROUPS: any[] = [];

const QuickSightReport: React.FC<QuickSightReportProps> = ({
  dashboardId,
  height = '600',
  width = '100%',
  parameters = NO_PARAMETERS,
  sheetId,
  filterGroups = NO_FILTER_GROUPS,
  style,
  visible = true,
  accountShortName,
  useRegisteredUser = false,
  userAlias,
  paramName,
  paramValues,
  paramPatches,
}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const experienceRef = useRef<DashboardExperience | null>(null);

  const [isLoading, setIsLoading] = useState(true); // initial embed
  const [isUpdating, setIsUpdating] = useState(false); // filter/param updates
  const [hasError, setHasError] = useState(false);

  const targetDashboardId = dashboardId || '';

  // ---------- Security helpers ----------
  function allowlistedQuickSightUrl(raw: string | undefined): string {
    if (!raw) throw new Error('No embed URL received');
    const url = new URL(raw);

    if (url.protocol !== 'https:') throw new Error('Embed URL must be https');

    // Allow both commercial & China partitions (regional or root)
    const hostOk =
      /(^|\.)quicksight\.amazonaws\.com$/.test(url.hostname) ||
      /(^|\.)quicksight\.amazonaws\.cn$/.test(url.hostname);
    if (!hostOk) throw new Error('Embed URL host not allowed');

    // Accept common embed routes
    const p = url.pathname;
    const pathOk =
      p.startsWith('/embed/') ||
      p.startsWith('/sn/embed/') ||
      /^\/sn\/account\/[^/]+\/dashboards\//.test(p);
    if (!pathOk) throw new Error('Unexpected embed path');

    return url.toString();
  }

  // Minimal character set for IDs (adjust if your ids can contain other chars)
  const sanitizeId = (v: unknown) => {
    const s = String(v ?? '').trim();
    return /^[A-Za-z0-9_-]+$/.test(s) ? s : null;
  };

  const sanitizeParamValues = useCallback((values: Array<string | number> | undefined) => {
    if (!Array.isArray(values)) return [];
    return values.map((v) => sanitizeId(v)).filter((v): v is string => Boolean(v));
  }, []);

  function sanitizeFilterGroups(input: any[] | undefined) {
    if (!Array.isArray(input)) return [];
    return input.map((g) => {
      const clone = { ...g };
      const f0 = clone?.Filters?.[0];
      if (f0?.CategoryFilter?.Configuration?.FilterListConfiguration?.CategoryValues) {
        const listCfg = f0.CategoryFilter.Configuration.FilterListConfiguration;
        const safeValues = sanitizeParamValues(listCfg.CategoryValues);
        clone.Filters = [
          {
            CategoryFilter: {
              ...f0.CategoryFilter,
              Configuration: {
                ...f0.CategoryFilter.Configuration,
                FilterListConfiguration: {
                  ...listCfg,
                  CategoryValues: safeValues,
                },
              },
            },
          },
        ];
      } else if (f0?.TimeRangeFilter) {
        const trf = f0.TimeRangeFilter;
        clone.Filters = [{ TimeRangeFilter: { ...trf } }];
      }
      return clone;
    });
  }

  const sanitizeParameters = useCallback(
    (input: Parameter[] | undefined): Parameter[] => {
      if (!Array.isArray(input)) return [];
      return input.map((p) => {
        const anyP: any = p;
        const out: any = { ...anyP };
        if (Array.isArray(anyP.Values)) {
          out.Values = sanitizeParamValues(anyP.Values);
        }
        return out as Parameter;
      });
    },
    [sanitizeParamValues],
  );

  /** Merge base parameters + optional injected parameter(s). */
  const mergedParameters: Parameter[] = React.useMemo(() => {
    const base = sanitizeParameters(parameters);

    if (paramPatches && typeof paramPatches === 'object') {
      for (const [name, vals] of Object.entries(paramPatches)) {
        const cleanVals = sanitizeParamValues(vals);
        if (cleanVals.length === 0) continue;
        const idx = base.findIndex((p) => p.Name === name);
        if (idx >= 0) base[idx] = { ...base[idx], Values: cleanVals };
        else base.push({ Name: name, Values: cleanVals } as Parameter);
      }
    }

    if (paramName && Array.isArray(paramValues) && paramValues.length > 0) {
      const values = sanitizeParamValues(paramValues);
      if (values.length > 0) {
        const idx = base.findIndex((p) => p.Name === paramName);
        if (idx >= 0) base[idx] = { ...base[idx], Values: values };
        else base.push({ Name: paramName, Values: values } as Parameter);
      }
    }

    return base;
  }, [paramPatches, paramValues, parameters, sanitizeParamValues, sanitizeParameters, paramName]);

  const safeFilterGroups = React.useMemo(
    () => sanitizeFilterGroups(filterGroups),
    // sanitizeFilterGroups is a stable module-local transform; key on the input.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [filterGroups],
  );
  const sheetIds: string[] = React.useMemo(
    () => (Array.isArray(sheetId) ? sheetId : sheetId ? [sheetId] : []),
    [sheetId],
  );

  // Match by FilterGroupId → (dataset+column) → column-only (fallback)
  function findMatchingFilterGroup(availableGroups: any[], incomingGroup: any) {
    if (incomingGroup?.FilterGroupId) {
      const byId = availableGroups.find((g) => g.FilterGroupId === incomingGroup.FilterGroupId);
      if (byId) return byId;
    }

    const f0 = incomingGroup?.Filters?.[0];
    const incomingFilter = f0?.CategoryFilter || f0?.TimeRangeFilter;
    const inCol = incomingFilter?.Column;
    const inName = inCol?.ColumnName as string | undefined;
    const inDs = inCol?.DataSetIdentifier as string | undefined;
    if (!inName) return null;

    let candidate =
      availableGroups.find((g) => {
        const gf0 = g?.Filters?.[0];
        const gf = gf0?.CategoryFilter || gf0?.TimeRangeFilter;
        const gCol = gf?.Column;
        return gCol?.ColumnName === inName && inDs && gCol?.DataSetIdentifier === inDs;
      }) || null;
    if (candidate) return candidate;

    candidate =
      availableGroups.find((g) => {
        const gf0 = g?.Filters?.[0];
        const gf = gf0?.CategoryFilter || gf0?.TimeRangeFilter;
        const gCol = gf?.Column;
        return gCol?.ColumnName === inName;
      }) || null;

    return candidate;
  }

  async function waitForFilterGroups(sid: string, tries = 10) {
    for (let i = 0; i < tries; i++) {
      const groups = await experienceRef.current!.getFilterGroupsForSheet(sid);
      if (Array.isArray(groups) && groups.length) return groups;
      await new Promise((r) => setTimeout(r, 200));
    }
    return [];
  }

  const applyParamsAndFilters = useCallback(async () => {
    if (!experienceRef.current) return;
    setIsUpdating(true);
    try {
      const anyExp: any = experienceRef.current;
      if (mergedParameters.length && typeof anyExp.setParameters === 'function') {
        await anyExp.setParameters(mergedParameters);
      }

      if (sheetIds.length && safeFilterGroups.length) {
        for (const sid of sheetIds) {
          const available = await waitForFilterGroups(sid);
          if (!available.length) continue;
          const updates: any[] = [];

          for (const incoming of safeFilterGroups) {
            const match = findMatchingFilterGroup(available, incoming);
            if (!match) continue;

            const inF0 = incoming.Filters?.[0];
            const mF0 = match.Filters?.[0];

            if (mF0?.CategoryFilter && inF0?.CategoryFilter?.Configuration) {
              updates.push({
                ...match,
                Filters: [
                  {
                    CategoryFilter: {
                      ...mF0.CategoryFilter,
                      Configuration: inF0.CategoryFilter.Configuration,
                    },
                  },
                ],
              });
            }

            if (mF0?.TimeRangeFilter && inF0?.TimeRangeFilter) {
              const incomingTRF = inF0.TimeRangeFilter;
              updates.push({
                ...match,
                Filters: [
                  {
                    TimeRangeFilter: {
                      ...mF0.TimeRangeFilter,
                      RangeMinimumValue:
                        incomingTRF.RangeMinimumValue ?? mF0.TimeRangeFilter.RangeMinimumValue,
                      RangeMaximumValue:
                        incomingTRF.RangeMaximumValue ?? mF0.TimeRangeFilter.RangeMaximumValue,
                      RollingDate: incomingTRF.RollingDate ?? mF0.TimeRangeFilter.RollingDate,
                      NullOption: incomingTRF.NullOption ?? mF0.TimeRangeFilter.NullOption,
                      TimeGranularity:
                        incomingTRF.TimeGranularity ?? mF0.TimeRangeFilter.TimeGranularity,
                    },
                  },
                ],
              });
            }
          }

          if (updates.length) {
            await experienceRef.current!.updateFilterGroups(updates);
          }
        }
      }
    } catch (error) {
      console.error('Error in applyParamsAndFilters:', error);
    } finally {
      setIsUpdating(false);
    }
  }, [mergedParameters, safeFilterGroups, sheetIds]);

  // ---------- 1) Embed once ----------
  useEffect(() => {
    let cancelled = false;

    const embed = async () => {
      try {
        setHasError(false);
        setIsLoading(true);

        if (
          experienceRef.current &&
          typeof (experienceRef.current as any).close === 'function'
        ) {
          try {
            await (experienceRef.current as any).close();
          } catch {}
        }
        if (containerRef.current) containerRef.current.replaceChildren();

        const out =
          useRegisteredUser && userAlias
            ? await generateEmbedUrlForRegisteredUser([targetDashboardId], userAlias, accountShortName)
            : await generateEmbedUrlForAnonymousUser([targetDashboardId], accountShortName);
        const rawUrl = (out as any).embdedUrl as string | undefined; // `embdedUrl` (sic) per service model
        const embedUrl = allowlistedQuickSightUrl(rawUrl);

        const ctx = await createEmbeddingContext();
        const frameOptions: FrameOptions = {
          url: embedUrl,
          container: containerRef.current!,
          height: typeof height === 'string' ? height : `${height}px`,
          width: typeof width === 'string' ? width : `${width}px`,
        };

        const contentOptions = {
          parameters: mergedParameters,
          locale: 'en-US',
          onMessage: async (messageEvent: EmbeddingEvents, _metadata?: ExperienceFrameMetadata) => {
            if (messageEvent.eventName === 'CONTENT_LOADED') {
              if (!cancelled) setIsLoading(false);
              try {
                await applyParamsAndFilters();
              } catch (e) {
                console.error('Initial apply failed:', e);
              }
            } else if (messageEvent.eventName === 'ERROR_OCCURRED') {
              if (!cancelled) {
                setHasError(true);
                setIsLoading(false);
              }
            } else if (messageEvent.eventName === 'MODAL_OPENED') {
              window.scrollTo({ top: 0 });
            }
          },
        };

        const exp = await ctx.embedDashboard(frameOptions, contentOptions);
        experienceRef.current = exp;
      } catch (err) {
        console.error('Error embedding QuickSight dashboard:', err);
        if (!cancelled) {
          setHasError(true);
          setIsLoading(false);
        }
      }
    };

    embed();

    return () => {
      cancelled = true;
      if (experienceRef.current && typeof (experienceRef.current as any).close === 'function') {
        try {
          (experienceRef.current as any).close();
        } catch {}
      }
    };
    // Embed once per dashboard/size — do NOT depend on params/filters here.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [targetDashboardId, height, width]);

  // ---------- 2) Re-apply when params/filters change ----------
  useEffect(() => {
    applyParamsAndFilters();
  }, [accountShortName, applyParamsAndFilters, mergedParameters, useRegisteredUser, userAlias]);

  // ---------- 3) Re-apply when becoming visible (e.g., after tab switch) ----------
  useEffect(() => {
    if (visible) {
      applyParamsAndFilters();
    }
  }, [visible, applyParamsAndFilters]);

  if (hasError) {
    return (
      <Box textAlign="center" padding="xl">
        <Box variant="h3" color="text-status-error">
          Failed to load QuickSight Dashboard
        </Box>
        <Box variant="p" color="text-status-error">
          Please check your permissions and try again.
        </Box>
        <Box margin={{ top: 'm' }}>
          <button onClick={() => window.location.reload()}>Retry</button>
        </Box>
      </Box>
    );
  }

  return (
    <div
      style={{
        position: 'relative',
        width: '100%',
        minHeight: typeof height === 'string' ? height : `${height}px`,
        ...style,
      }}
    >
      {(isLoading || isUpdating) && (
        <Box textAlign="center" padding="xl">
          <Spinner size="large" />
          <Box variant="p" margin={{ top: 's' }}>
            {isLoading ? 'Loading QuickSight Dashboard...' : 'Updating filters...'}
          </Box>
        </Box>
      )}

      <div
        ref={containerRef}
        style={{
          width: '100%',
          height: typeof height === 'string' ? height : `${height}px`,
          minHeight: typeof height === 'string' ? height : `${height}px`,
          border: '1px solid #e1e4e8',
          borderRadius: '8px',
          display: isLoading ? 'none' : 'block',
          overflow: 'auto',
        }}
      />
    </div>
  );
};

export default QuickSightReport;
